import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ClimbingArea, AreaDetail } from "../types"
import { areaLink } from "../utils/climbingAreaUtils";
import fetchAreaDetail from "../api/areaDetail";
import fetchAreaDetailExtended from "../api/climateStation";
import { isNotFound } from "../api/apiError";
import NotFoundPage from "./notFound";
import { LoadError } from "../components/loadError";
import { trackAreaView } from "../utils/areaHistory";
import { PlaceSchema, BreadcrumbSchema } from "../components/structuredData";
import { useSeoMeta } from "../hooks/useSeoMeta";
import { AreaHero } from "../components/area/areaHero";
import { HourlyCard } from "../components/area/hourlyCard";
import { WeekCard } from "../components/area/weekCard";
import { SeasonCard } from "../components/area/seasonCard";
import { AboutCard } from "../components/area/aboutCard";
import { parseNumber } from "../utils/conditions";
import "../components/area/areaPage.css";

interface ClimbingAreaPageProps { areaId: string }
const ClimbingAreaPage = ({ areaId }: ClimbingAreaPageProps) => {

  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [result, setResult] = useState<ClimbingArea | null>(null);
  const [areaDetailExtended, setAreaDetailExtended] = useState<AreaDetail | null>(null);
  const [extendedStatus, setExtendedStatus] = useState<"loading" | "loaded" | "notFound" | "failed">("loading");
  // Bumped by "Try again" to refetch
  const [attempt, setAttempt] = useState(0);

  // The forecast endpoint answers 500 for an unknown area, so "not found" comes from
  // the detail endpoint's 404. Area IDs are numeric, so anything else can't exist.
  const validId = /^\d+$/.test(areaId);
  const notFound = !validId || (error !== undefined && extendedStatus === "notFound");

  // Build SEO data from results
  const areaName = result?.name || 'Climbing Area';
  const stateName = result?.adminAreaName || '';
  const description = areaDetailExtended?.description
    ? areaDetailExtended.description.substring(0, 160)
    : `Weather forecast for ${areaName} climbing area in ${stateName}. Get hourly and daily forecasts to plan your climbing trip.`;

  // The full detail URL is canonical; it's what the sitemaps and internal links use.
  // Until the area loads, the path being viewed is the best guess.
  const location = useLocation();
  const navigate = useNavigate();
  const canonicalPath = result ? areaLink(result) : location.pathname;

  // Runs after NotFoundPage's own useSeoMeta (parent effects run last), so it has
  // to carry the not-found title and noindex too
  useSeoMeta({
    title: notFound ? 'Page not found - ClimbingWeather.com' : result ? `${areaName} - Daily Climbing Forecast` : 'Loading...',
    description,
    canonicalPath,
    noIndex: notFound
  });

  // Short or outdated area URLs (/area/123, old hourly links, renamed areas) are
  // swapped for the canonical one so shared and bookmarked links stay consistent
  useEffect(() => {
    if (result && !samePath(location.pathname, areaLink(result))) {
      navigate(areaLink(result) + location.search + location.hash, { replace: true });
    }
    // Only when a newly loaded area arrives, not on every location change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  useEffect(() => {
    // Reset state when areaId changes
    setIsLoaded(false);
    setError(undefined);
    setResult(null);
    setAreaDetailExtended(null);
    setExtendedStatus("loading");

    if (!/^\d+$/.test(areaId)) {
      return;
    }

    // Fetch area detail (with forecast)
    fetchAreaDetail(areaId)
    .then(
      (result) => {
        setIsLoaded(true);
        setResult(result);
      },
      (error) => {
        setIsLoaded(true);
        setError(error);
      }
    );

    // Fetch extended area detail with climate station data
    fetchAreaDetailExtended(areaId)
    .then(
      (detail) => {
        setAreaDetailExtended(detail);
        setExtendedStatus("loaded");
      },
      (error) => {
        setAreaDetailExtended(null);
        setExtendedStatus(isNotFound(error) ? "notFound" : "failed");
      }
    );
  }, [areaId, attempt]);

  useEffect(() => {
    if (result) {
      // Track this area view for history
      trackAreaView(result);
    }
  }, [result]);

  if (notFound) {
    return <NotFoundPage title="We couldn’t find that area" message="It may have been removed, or the link is mistyped." />;
  }

  if (error) {
    // Wait for the detail endpoint before deciding between "not found" and "error"
    if (extendedStatus === "loading") {
      return <AreaPageSkeleton />;
    }
    return (
      <div className="cw-page area-page">
        <div className="area-page__body">
          <LoadError title="We couldn’t load this forecast" headingLevel={1} onRetry={() => setAttempt((n) => n + 1)} />
        </div>
      </div>
    );
  }

  if (!isLoaded || result === null) {
    return <AreaPageSkeleton />;
  }

  const area = result;
  const forecast = area.forecast;
  const now = Date.now() / 1000;
  return (
    <div className="cw-page area-page">
      {areaDetailExtended && (
        <>
          <PlaceSchema
            name={area.name}
            description={areaDetailExtended.description || undefined}
            latitude={Number(areaDetailExtended.latitude)}
            longitude={Number(areaDetailExtended.longitude)}
            elevation={areaDetailExtended.elevation ? Number(areaDetailExtended.elevation) : undefined}
            state={area.adminAreaName}
            url={`https://climbingweather.com${canonicalPath}`}
          />
          <BreadcrumbSchema
            items={[
              { name: 'Home', url: 'https://climbingweather.com/' },
              { name: area.adminAreaName, url: `https://climbingweather.com/forecast-by-state/${area.adminArea}` },
              { name: area.name, url: `https://climbingweather.com${canonicalPath}` }
            ]}
          />
        </>
      )}
      <AreaHero area={area} detail={areaDetailExtended} now={now} />
      <div className="area-page__body">
        {forecast ? (
          <>
            <HourlyCard forecast={forecast} now={now} />
            <WeekCard forecast={forecast} now={now} />
          </>
        ) : (
          <section className="cw-card area-page__week">
            <p className="cw-card__message">The forecast isn’t available for this area right now.</p>
          </section>
        )}
        <div className="area-page__side">
          <SeasonCard
            station={areaDetailExtended?.clim81Station}
            stationDistance={areaDetailExtended?.clim81StationDistance}
            areaElevation={parseNumber(areaDetailExtended?.elevation)}
          />
          {areaDetailExtended && <AboutCard detail={areaDetailExtended} />}
        </div>
      </div>
    </div>
  );

};

/// Compare paths ignoring percent-encoding differences ("Devil%27s" vs "Devil's")
function samePath(a: string, b: string): boolean {
  const decode = (path: string) => {
    try {
      return decodeURIComponent(path);
    } catch {
      return path;
    }
  };
  return decode(a) === decode(b);
}

function AreaPageSkeleton() {
  return (
    <div className="cw-page area-page" aria-busy="true">
      <span className="cw-sr-only" role="status">Loading forecast</span>
      <div className="area-skeleton__hero" />
      <div className="area-page__body">
        <div className="cw-card area-page__hourly">
          <div className="cw-skeleton cw-skeleton--short" />
          <div className="cw-skeleton cw-skeleton--tall" />
        </div>
        <div className="cw-card area-page__week">
          {[...Array(5)].map((_, i) => <div key={i} className="cw-skeleton" />)}
        </div>
      </div>
    </div>
  );
}

export default ClimbingAreaPage;
